import { useFlag } from '@unleash/proxy-client-react';
import { Col, Divider, Row, Tooltip } from 'antd';
import clipboard from 'clipboard';
import Image from 'next/image';
import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmailIcon,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import styled from 'styled-components';

import { QuestionMarkSvg } from '../../../assets/icons';
import LinkSvg from '../../../assets/icons/LinkSvg';
import emptyTemplateCoverPhoto from '../../../assets/images/emptyTemplateCoverPhoto.png';
import theme from '../../../assets/theme';
import useCreateOrEditGenerator from '../../../hooks/useCreateOrEditGenerator';
import {
  TemplateAction,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import { useGameMediaFileUploader } from '../../../hooks/useGameMediaFileUploader';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useRunTestcases from '../../../hooks/useRunTestcases';
import useStandards from '../../../hooks/useStandards';
import useTestcases from '../../../hooks/useTestcases';
import useUploadGeneratorImage from '../../../hooks/useUploadGeneratorCoverSvg';
import {
  orderCCSSELAStandards,
  orderTEKSELAStandards,
} from '../../../pages/[tenantId]/browseByStandard';
import backendService from '../../../services/backendService';
import {
  AugmentedStandards,
  DiscoveredTemplateStatus,
  Standards,
} from '../../../services/backendService/types';
import {
  GeneratorCoarseLicenseLevel,
  Template,
} from '../../../services/TemplateService/Template.type';
import { getBaseUrl, isDevelopment } from '../../../utils/client';
import { generateUniqueId } from '../../../utils/general';
import { gentlyCheckDestinationTenantUrl } from '../../../utils/navigatorUtils';
import {
  augmentRawStandard,
  sortAugmentedStandardsInplace,
} from '../../../utils/standardsStrandParsingUtils';
import { getTitle } from '../../../utils/templateUtils';
import { Input } from '../../_atoms/_Input';
import MultiSelect from '../../_atoms/_MultiSelect/MultiSelect';
import MultiSelectCustomTags from '../../_atoms/_MultiSelectCustomTags/_MultiSelectCustomTags';
import Select from '../../_atoms/_Select/Select';
import { Switch } from '../../_atoms/_Switch';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../BaseModal';
import CoverPhotoEditor from '../CoverPhotoEditor';
import GameImageRenderer from '../GameImageRenderer';
import MSTeamsShareButton from '../MSTeamsShareButton';
import QRCodeShareButton from '../QRCodeShareButton';
import ManageStandardsForm from './ManageStandardsForm';

const MAX_GENERATOR_COVER_FILE_SIZE_MB = 5;

const SettingsModalDesigner = ({
  closeModal,
  openModal,
  templateId,
  localTemplate,
  localTemplateDispatch,
  isSavingTemplate,
}: {
  templateId: string;
  closeModal: () => void;
  openModal: boolean;
  title?: string | null;
  localTemplate: Template;
  localTemplateDispatch: Dispatch<TemplateAction>;
  isSavingTemplate: boolean;
}) => {
  const metrics = useMetrics();

  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  const isAllowedToPublishGenerator = useFlag('isAllowedToPublishGenerator');

  const isGeneratedByAI = useDesignerDataContext(
    c => !!c.state.localTemplate?.metadata?.['aiTopic'],
  );

  const templateRef = useRef(new clipboard('#copy_url_template'));
  const [copyTemplateSuccess, setCopyTemplateSuccess] = useState(false);
  const [openCoverPhotoModal, setOpenCoverPhotoModal] = useState(false);
  const [
    isCoverPhotoUploadSizeErrorVisible,
    setIsCoverPhotoUploadSizeErrorVisible,
  ] = useState(false);
  const { tenantId } = useRouterMatch();
  const { upload, isUploading } = useGameMediaFileUploader(tenantId);
  const { t } = useTranslation('Host');

  const {
    isEditingOrCreatingGenerator,
    save: saveGenerator,
    generator,
    isGenerator,
  } = useCreateOrEditGenerator(templateId);

  const { standards: rawStandards, isFetchingStandards } = useStandards({
    isGenerator,
  });
  const standards: AugmentedStandards[] = useMemo(() => {
    if (isFetchingStandards) return [];
    const augmentedStandards: AugmentedStandards[] = rawStandards
      ?.map((rawStandard: Standards) => {
        return augmentRawStandard(rawStandard);
      })
      ?.filter(Boolean) as AugmentedStandards[];
    sortAugmentedStandardsInplace(augmentedStandards);
    return augmentedStandards;
  }, [rawStandards, isFetchingStandards]);

  const orderedStandards = useMemo(() => {
    if (isFetchingStandards) return [];

    const selectedStandards = generator?.learningStandards?.map(standard => standard.id);
    const selectedStandardsFiltered = standards.filter(standard =>
      selectedStandards?.includes(standard.id),
    );

    const selectedTeksStandards = selectedStandardsFiltered.filter(
      standard => standard.standardsSet === 'TEKS',
    );

    const selectedCcssStandards = selectedStandardsFiltered.filter(
      standard => standard.standardsSet === 'CCSS',
    );

    const nonSelectedStandards = standards.filter(
      standard => !selectedStandards?.includes(standard.id),
    );

    const teksStandards = nonSelectedStandards.filter(
      standard => standard.standardsSet === 'TEKS',
    );

    const ccssStandards = nonSelectedStandards.filter(
      standard => standard.standardsSet === 'CCSS',
    );

    return [
      ...selectedTeksStandards,
      ...selectedCcssStandards,
      ...teksStandards,
      ...ccssStandards,
    ];
  }, [generator?.learningStandards, standards, isFetchingStandards]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof Template,
  ) => {
    if (e.target.value) {
      e.persist();
      if (key === 'title') {
        localTemplateDispatch({
          type: TemplateStateActions.titleChanged,
          value: e.target.value,
        });
      }
      if (key === 'description') {
        localTemplateDispatch({
          type: TemplateStateActions.descriptionChanged,
          value: e.target.value,
        });
      }
      if (key === 'coverImageAltText') {
        localTemplateDispatch({
          type: TemplateStateActions.coverImageAltTextChanged,
          value: e.target.value,
        });
      }
    } else {
      if (key === 'title') {
        localTemplateDispatch({
          type: TemplateStateActions.titleChanged,
          value: undefined,
        });
      }
      if (key === 'description') {
        localTemplateDispatch({
          type: TemplateStateActions.descriptionChanged,
          value: undefined,
        });
      }
      if (key === 'coverImageAltText') {
        localTemplateDispatch({
          type: TemplateStateActions.coverImageAltTextChanged,
          value: undefined,
        });
      }
    }
  };

  const handleSubjectsSelected = (subjects: string[]) => {
    localTemplateDispatch({ type: TemplateStateActions.changeSubjects, subjects });
  };

  const handleGradesSelected = (grades: string[]) => {
    localTemplateDispatch({ type: TemplateStateActions.changeGrades, grades });
  };

  const handleCategoriesSelected = (categories: string[]) => {
    localTemplateDispatch({
      type: TemplateStateActions.editGeneratorCategories,
      categories: categories.map(c => ({
        name: c,
        value: c.toLowerCase().replaceAll(' ', '_'),
      })),
    });
  };

  const handleRealNamesSelect = (requireRealNames: boolean) => {
    localTemplateDispatch({
      type: TemplateStateActions.realNamesChanged,
      require: requireRealNames,
    });
  };

  useEffect(() => {
    const clip = templateRef.current;
    clip.on('success', () => {
      localTemplateDispatch({
        type: TemplateStateActions.isBlockedFromSharingChanged,
        isBlockedFromSharing: false,
      });
      setCopyTemplateSuccess(true);
    });

    return () => {
      clip.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copyTemplateSuccess) {
        setCopyTemplateSuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copyTemplateSuccess]);

  const handleSave = () => {
    closeModal();
  };

  const handleOpenCoverPhotoModal = () => {
    setOpenCoverPhotoModal(true);
  };

  const handlePhotoUpload = (data: { src: string; mimeType: string }) => {
    const fileId = generateUniqueId().toString();
    upload({
      imgData: data.src,
      fileId,
      onSuccessFunction: () => {
        localTemplateDispatch({
          type: TemplateStateActions.coverPhotoChanged,
          coverImageId: fileId,
          coverImageAltText: localTemplate.coverImageAltText || undefined,
        });

        setOpenCoverPhotoModal(false);
      },
    });
  };

  const handleIsBlockedFromSharingChange = (isBlockedFromSharing: boolean) => {
    localTemplateDispatch({
      type: TemplateStateActions.isBlockedFromSharingChanged,
      isBlockedFromSharing,
    });
    metrics.logEvent('Profile.ToggleLinkSharing', {
      value: isBlockedFromSharing,
    });
  };

  const { upload: uploadGeneratorImage } = useUploadGeneratorImage(tenantId);

  const [generatorSettings, setGeneratorSettings] = useState<
    | {
        status: DiscoveredTemplateStatus;
        isPremiumGenerator: boolean;
        generatorPlan: GeneratorCoarseLicenseLevel | undefined;
        learningStandards: string[];
        durationInMins?: number;
      }
    | undefined
  >();

  useEffect(() => {
    setGeneratorSettings({
      status: generator?.status || 'staging',
      isPremiumGenerator: generator?.aiContent?.isPremiumGenerator || false,
      generatorPlan: generator?.aiContent?.generatorPlan || undefined,
      learningStandards: generator?.learningStandards?.map(each => each.id) || [],
      durationInMins: generator?.aiContent?.durationInMins || undefined,
    });
  }, [
    generator?.status,
    generator?.aiContent?.isPremiumGenerator,
    generator?.aiContent?.generatorPlan,
    generator?.learningStandards,
    generator?.aiContent?.durationInMins,
  ]);

  const handleUploadGeneratorCoverImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    const file = files?.[0];
    if (file) {
      if (file.size > MAX_GENERATOR_COVER_FILE_SIZE_MB * 1024 * 1024) {
        setIsCoverPhotoUploadSizeErrorVisible(true);
        return;
      }
      const newGeneratorCoverImage = `${generateUniqueId().toString()}.${file.name
        .split('.')
        .pop()}`;
      uploadGeneratorImage({
        file: file,
        coverImageId: newGeneratorCoverImage,
        imageType: 'generatorCoverImage',
        onSuccessFunction: (coverImageId: string) => {
          setIsCoverPhotoUploadSizeErrorVisible(false);
          localTemplateDispatch({
            type: TemplateStateActions.coverPhotoChanged,
            coverImageId: coverImageId,
            coverImageAltText: localTemplate.coverImageAltText || undefined,
          });
          if (!generatorSettings) return;
          saveGenerator({
            generatorId: localTemplate.id,
            status: generatorSettings.status,
            isPremiumGenerator: generatorSettings.isPremiumGenerator,
            generatorPlan: generatorSettings.generatorPlan,
            coverImageId: coverImageId,
            coverImageAltText: localTemplate.coverImageAltText || undefined,
          });
        },
      });
    }
  };

  const handleUploadGeneratorBackgroundImage = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { files } = e.target;
    const file = files?.[0];
    if (file) {
      if (file.size > MAX_GENERATOR_COVER_FILE_SIZE_MB * 1024 * 1024) {
        setIsCoverPhotoUploadSizeErrorVisible(true);
        return;
      }
      const newGeneratorBackgroundImage = `${generateUniqueId().toString()}.${file.name
        .split('.')
        .pop()}`;
      uploadGeneratorImage({
        file: file,
        coverImageId: newGeneratorBackgroundImage,
        imageType: 'generatorBackgroundImage',
        onSuccessFunction: (backgroundImageId: string) => {
          setIsCoverPhotoUploadSizeErrorVisible(false);

          if (!generatorSettings) return;
          saveGenerator({
            generatorId: localTemplate.id,
            status: generatorSettings.status,
            isPremiumGenerator: generatorSettings.isPremiumGenerator,
            generatorPlan: generatorSettings.generatorPlan,
            backgroundImageId: backgroundImageId,
            coverImageAltText: localTemplate.coverImageAltText || undefined,
            learningStandards: generatorSettings.learningStandards,
            durationInMins: generatorSettings.durationInMins,
          });
        },
      });
    }
  };

  const generatorStatusOptions = useMemo(() => {
    if (isAllowedToPublishGenerator || isDevelopment())
      return [
        {
          displayName: 'Published',
          value: 'published',
        },
        {
          displayName: 'Staging',
          value: 'staging',
        },
        {
          displayName: 'Not published',
          value: 'unPublishedByOwner',
        },
      ];
    return [
      {
        displayName: 'Staging',
        value: 'staging',
      },
      {
        displayName: 'Not published',
        value: 'unPublishedByOwner',
      },
    ];
  }, [isAllowedToPublishGenerator]);

  const generatorPlanOptions = [
    {
      displayName: 'Freemium',
      value: 'freemium',
    },
    {
      displayName: 'Premium',
      value: 'premium',
    },
    {
      displayName: 'School plan',
      value: 'enterprise',
    },
  ];

  const generatorCoverImageUrl = useMemo(() => {
    if (!generator || !generator.coverImageId) return null;
    return backendService.instance.generatorPlugin.getGeneratorThumbnailUrl(
      generator.coverImageId,
    );
  }, [generator]);

  const generatorBackgroundImageUrl = useMemo(() => {
    if (!generator || !generator.aiContent?.backgroundImageId) return null;
    const backgroundUrl = backendService.instance.generatorPlugin.getGeneratorThumbnailUrl(
      generator.aiContent?.backgroundImageId,
    );
    return backgroundUrl;
  }, [generator]);

  const { runTestcases } = useRunTestcases();

  const { testcases } = useTestcases({
    presentationId: templateId,
  });

  const syncSampleLessons = () => {
    if (!testcases) return;
    const sampleLessonTestCases = testcases.filter(testcase => testcase.isSampleLesson);
    runTestcases({
      testcaseIds: sampleLessonTestCases.map(testcase => testcase.id),
      presentationId: templateId,
      useSlidesAsSampleLesson: true,
    });
  };

  const handleClickShareButton = useCallback(
    (type: string) => {
      localTemplateDispatch({
        type: TemplateStateActions.isBlockedFromSharingChanged,
        isBlockedFromSharing: false,
      });
      metrics.logEvent('Share.Social.Media.Click', { someType: type });
    },
    [metrics, localTemplateDispatch],
  );

  const lessonTitle = useMemo(
    () =>
      getTitle({
        title: localTemplate?.title,
        slides: localTemplate?.content.slides,
        maxLength: 30,
      }),
    [localTemplate?.title, localTemplate?.content.slides],
  );

  const shareUrl = isGenerator
    ? `${getBaseUrl()}/generators/${lessonTitle
        .replace(/\s/g, '-')
        .replace(/[^\w\s\-]/gi, '')}/g/${templateId}`
    : `${getBaseUrl()}/${gentlyCheckDestinationTenantUrl()}/lessons/${templateId}?showpreview=true`;

  const shareTitle = isGeneratedByAI
    ? `${t('This lesson was generated by AI in less than 10 seconds')}`
    : '\n';

  const emailSubject = isGeneratedByAI
    ? t('I generated this lesson in 10 seconds with Curipod')
    : t('Check out my Curipod lesson');

  const handleExportAsPdf = useDesignerDataContext(context => context.handleExportAsPdf);

  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  const handleDownloadPDFClicked = () => {
    if (!handleExportAsPdf) return;
    if (isDownloadingPDF) return;
    setIsDownloadingPDF(true);
    metrics.logEvent('Download.PDF.Designer', { clicked: 'Download' });
    handleExportAsPdf({
      title: lessonTitle,
      onSuccess: () => {
        setIsDownloadingPDF(false);
      },
    });
  };

  return (
    <BaseModal
      title={isGenerator ? t('Customizable lesson settings') : t('Lesson settings')}
      visible={openModal}
      width="800px"
      onCancel={closeModal}
      style={{ top: 40 }}
    >
      <>
        {openCoverPhotoModal && (
          <BaseModal
            title={t('Cover photo')}
            visible={openCoverPhotoModal}
            width="600px"
            style={{ top: 150 }}
            onCancel={() => setOpenCoverPhotoModal(false)}
            maskStyle={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
            zIndex={2000}
          >
            <CoverPhotoEditor
              isUploading={isUploading || isSavingTemplate}
              onUpload={handlePhotoUpload}
            />
          </BaseModal>
        )}
        <Row style={{ marginTop: 20 }} gutter={[40, 40]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <Row gutter={[20, 20]}>
              <Col span={24}>
                <Title $fontSize={18}>
                  {t('Title')}
                  <Tooltip title={t('Title')}>
                    <TooltipIconWrapper>
                      <QuestionMarkSvg />
                    </TooltipIconWrapper>
                  </Tooltip>
                </Title>
                <Input
                  onChange={e => {
                    handleInputChange(e, 'title');
                  }}
                  value={!!localTemplate?.title ? localTemplate.title : ''}
                />
              </Col>
              <Col span={24}>
                <Title $fontSize={18}>
                  {t('Description (optional)')}
                  <Tooltip title={t('Description')}>
                    <TooltipIconWrapper>
                      <QuestionMarkSvg />
                    </TooltipIconWrapper>
                  </Tooltip>
                </Title>
                <TextArea
                  rows={4}
                  fluid
                  onChange={e => {
                    handleInputChange(e, 'description');
                  }}
                  value={localTemplate?.description || undefined}
                />
              </Col>

              <Col span={24} style={{ marginTop: 3 }}>
                <Title $fontSize={18}>
                  {t('Require real names')}
                  <Tooltip
                    title={t(
                      t(
                        'RequireParticipantRealName',
                        'Require your participants to write their real name. Real names are only visible to you as the host.',
                      ),
                    )}
                  >
                    <TooltipIconWrapper>
                      <QuestionMarkSvg />
                    </TooltipIconWrapper>
                  </Tooltip>
                </Title>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Switch
                    color={theme.colors.primary}
                    value={localTemplate && localTemplate.requireRealNames}
                    onChange={bool => {
                      handleRealNamesSelect(bool);
                      metrics.logEvent('Creator.ToggleRealNames', {
                        value: bool,
                        templateId,
                      });
                    }}
                  />
                  <span style={{ marginLeft: 10, fontWeight: 'bold' }}>
                    {localTemplate && localTemplate.requireRealNames
                      ? t('On (participants write their real name)')
                      : t('Off')}
                  </span>
                </div>
              </Col>
              {isGenerator && (
                <Col span={24}>
                  <Title $fontSize={18}>Subjects</Title>
                  <MultiSelect
                    style={{ fontSize: 16, paddingBottom: 3 }}
                    value={localTemplate?.aiContent?.subjects || []}
                    onChange={e => handleSubjectsSelected(e as string[])}
                    options={[
                      {
                        displayName: 'English & Language Arts',
                        value: 'ela',
                      },
                      {
                        displayName: 'Math',
                        value: 'math',
                      },
                      {
                        displayName: 'Social studies',
                        value: 'social_studies',
                      },
                      {
                        displayName: 'Science',
                        value: 'science',
                      },
                      {
                        displayName: 'World languages',
                        value: 'world_languages',
                      },
                      {
                        displayName: 'Mental health',
                        value: 'mental_health',
                      },
                      {
                        displayName: 'Other',
                        value: 'other',
                      },
                    ]}
                  />
                </Col>
              )}
              {isGenerator && (
                <Col span={24}>
                  <Title $fontSize={18}>Grades</Title>
                  <MultiSelect
                    style={{ fontSize: 16, paddingBottom: 3 }}
                    value={localTemplate?.aiContent?.grades || []}
                    onChange={e => handleGradesSelected(e as string[])}
                    options={[
                      {
                        displayName: '12th',
                        value: '12',
                      },
                      {
                        displayName: '11th',
                        value: '11',
                      },
                      {
                        displayName: '10th',
                        value: '10',
                      },
                      {
                        displayName: '9th',
                        value: '9',
                      },
                      {
                        displayName: '8th',
                        value: '8',
                      },
                      {
                        displayName: '7th',
                        value: '7',
                      },
                      {
                        displayName: '6th',
                        value: '6',
                      },
                      {
                        displayName: '5th',
                        value: '5',
                      },
                      {
                        displayName: '4th',
                        value: '4',
                      },
                      {
                        displayName: '3rd',
                        value: '3',
                      },
                      {
                        displayName: '2nd',
                        value: '2',
                      },
                      {
                        displayName: '1st',
                        value: '1',
                      },
                      {
                        displayName: 'Kindergarten',
                        value: '0',
                      },
                    ]}
                  />
                </Col>
              )}
              {isGenerator && (
                <Col span={24}>
                  <Title $fontSize={18}>Categories</Title>
                  <MultiSelectCustomTags
                    style={{ fontSize: 16, paddingBottom: 3 }}
                    value={localTemplate?.aiContent?.categories.map(c => c.value) || []}
                    onChange={e => handleCategoriesSelected(e as string[])}
                    options={[
                      {
                        displayName: 'Story-Based Writing Practice',
                        value: 'quick_writes',
                      },
                      {
                        displayName: 'Argumentative writing',
                        value: 'argumentative_writing',
                      },
                      {
                        displayName: 'Narrative writing',
                        value: 'narrative_writing',
                      },
                      {
                        displayName: 'Informational writing',
                        value: 'informational_writing',
                      },
                      {
                        displayName: 'Vocabulary practice',
                        value: 'vocabulary_practice',
                      },
                      {
                        displayName: 'Exit tickets',
                        value: 'exit_tickets',
                      },
                      {
                        displayName: '5 Min Writing Practice',
                        value: 'five_min_writing',
                      },
                      {
                        displayName: 'Social emotional learning',
                        value: 'social_emotional_learning',
                      },
                      {
                        displayName: 'Draw it!',
                        value: 'draw_it',
                      },
                      {
                        displayName: 'Games & competitions',
                        value: 'games_and_competitions',
                      },
                      {
                        displayName: 'Curi-quiz',
                        value: 'curi_quiz',
                      },
                      {
                        displayName: 'Making inferences',
                        value: 'making_inferences',
                      },
                      {
                        displayName: 'Vocabulary games',
                        value: 'vocabulary_games',
                      },
                      {
                        displayName: 'Escape rooms',
                        value: 'escape_rooms',
                      },
                      {
                        displayName: 'Toolkit',
                        value: 'toolkit',
                      },
                      {
                        displayName: 'Bilingual',
                        value: 'bilingual',
                      },
                      {
                        displayName: 'Write more (deprecated)',
                        value: 'write_more',
                      },
                      {
                        displayName: 'College entrance exams',
                        value: 'college_entrance_exams',
                      },
                      {
                        displayName: 'SBAC',
                        value: 'sbac',
                      },
                      {
                        displayName: 'Reading comprehension (deprecated)',
                        value: 'reading_comprehension',
                      },
                      {
                        displayName: 'Vocabulary & grammar (deprecated)',
                        value: 'vocabulary_and_grammar',
                      },
                      {
                        displayName: 'Students receive AI feedback (main category)',
                        value: 'ai_feedback',
                      },
                      {
                        displayName: 'STAAR Prep (old)',
                        value: 'staar_prep',
                      },
                      {
                        displayName: 'Test Prep',
                        value: 'test_prep_made_fun',
                      },
                      {
                        displayName: 'TEXAS - STAAR & TELPAS',
                        value: 'staar',
                      },
                      {
                        displayName: 'TELPAS',
                        value: 'telpas',
                      },
                      {
                        displayName: 'M-STEP',
                        value: 'mstep',
                      },
                      {
                        displayName: 'WIDA',
                        value: 'wida',
                      },
                      {
                        displayName: 'CALIFORNIA - SBAC & CAASPP',
                        value: 'california_sbac_caaspp',
                      },
                      {
                        displayName: 'NEW YORK - NYS Exam Prep',
                        value: 'ny_stp',
                      },
                      {
                        displayName: 'Georgia - Milestones',
                        value: 'georgia_gmas',
                      },
                      {
                        displayName: 'WOW',
                        value: 'wow',
                      },
                      {
                        displayName: 'Daily routines',
                        value: 'daily_routines',
                      },
                      {
                        displayName: 'Full lessons',
                        value: 'full_lessons',
                      },
                      {
                        displayName: 'Brain breaks',
                        value: 'brain_breaks',
                      },
                      {
                        displayName: 'Curify',
                        value: 'curify',
                      },
                      {
                        displayName: 'Creative writing',
                        value: 'creative_writing',
                      },
                      {
                        displayName: 'Daily writing practice',
                        value: 'writing_routines',
                      },
                      {
                        displayName: 'Students receive AI Feedback',
                        value: 'students_get_ai_feedback',
                      },
                      {
                        displayName: 'Show comprehension by drawing',
                        value: 'show_comprehension_by_drawing',
                      },
                      {
                        displayName: 'Activities based on text resource',
                        value: 'i_have_my_text',
                      },
                      {
                        displayName: 'Generate new text resource',
                        value: 'generate_my_text',
                      },
                      {
                        displayName: 'Exploring figurative language',
                        value: 'figurative_language',
                      },
                      {
                        displayName: 'Vocabulary practice',
                        value: 'content_specific_vocabulary',
                      },
                      {
                        displayName: 'Quick fun',
                        value: 'quick_fun',
                      },
                      {
                        displayName: 'Spark curiosity',
                        value: 'spark_curiosity',
                      },
                      {
                        displayName: 'Check in & out',
                        value: 'check_in_and_out',
                      },
                      {
                        displayName: 'Rigorous students',
                        value: 'rigorous_students',
                      },
                      {
                        displayName: 'Daily writing practice',
                        value: 'daily_writing_practice',
                      },
                      {
                        displayName: 'Students use feedback to improve',
                        value: 'students_use_feedback_to_improve',
                      },
                      {
                        displayName: 'Frequent feedback opportunities',
                        value: 'frequent_feedback_opportunities',
                      },
                      {
                        displayName: 'Captivating writing prompts',
                        value: 'captivating_writing_prompts',
                      },
                      {
                        displayName: 'Students decide input',
                        value: 'students_decide_input',
                      },
                      {
                        displayName: 'Amazing student drawings',
                        value: 'amazing_student_drawings',
                      },
                      {
                        displayName: 'Brain breaks and energizers',
                        value: 'brain_breaks_and_energizers',
                      },
                      {
                        displayName: 'Morning meeting',
                        value: 'morning_meeting',
                      },
                      {
                        displayName: 'Bell ringers',
                        value: 'bell_ringers',
                      },
                      {
                        displayName: 'Start and end the lesson',
                        value: 'start_and_end_the_lesson',
                      },
                      {
                        displayName: 'Just for fun',
                        value: 'just_for_fun',
                      },
                      {
                        displayName: 'Academic',
                        value: 'academic',
                      },
                      {
                        displayName: 'Write in science',
                        value: 'write_in_science',
                      },
                      {
                        displayName: 'Formative assessment',
                        value: 'formative_assessment',
                      },
                      {
                        displayName: 'Turn and talk',
                        value: 'turn_and_talk',
                      },
                      {
                        displayName: 'For the sub',
                        value: 'for_the_sub',
                      },
                      {
                        displayName: 'Classroom buzz',
                        value: 'classroom_buzz',
                      },
                      {
                        displayName: 'Critical thinking',
                        value: 'critical_thinking',
                      },
                      {
                        displayName: 'RLA  (STAAR)',
                        value: 'staar_ela',
                      },
                      {
                        displayName: 'RLA Spanish  (STAAR)',
                        value: 'staar_rla_spanish',
                      },
                      {
                        displayName: 'Social Studies & U.S. History (STAAR)',
                        value: 'staar_social_studies',
                      },
                      {
                        displayName: 'Science (STAAR)',
                        value: 'staar_science',
                      },
                      {
                        displayName: 'Math (STAAR)',
                        value: 'staar_math',
                      },
                      {
                        displayName: 'Arizona AASA',
                        value: 'arizona_aasa',
                      },
                      {
                        displayName: 'Ohio OST',
                        value: 'ohio_ost',
                      },
                      {
                        displayName: 'Ohio OELPA',
                        value: 'ohio_oelpa',
                      },
                      {
                        displayName: 'West Virginia WVGSA',
                        value: 'west_virginia_wvgsa',
                      },
                      {
                        displayName: 'West Virginia ELPA21',
                        value: 'westvirginia_elpa21',
                      },
                      {
                        displayName: 'Colorado - CMAS',
                        value: 'colorado_cmas',
                      },
                      {
                        displayName: 'North Carolina Test Prep',
                        value: 'north_carolina_test_prep',
                      },
                      {
                        displayName: 'Pennsylvania - PSSA',
                        value: 'pennsylvania_pssa',
                      },
                      /* Below are the category tags we used before we added elastic search engine */
                      { displayName: 'All (deprecated)', value: 'all' },
                      { displayName: 'Introduction (deprecated)', value: 'introduction' },
                      { displayName: 'Content (deprecated)', value: 'content' },
                      { displayName: 'SEL (deprecated)', value: 'sel' },
                      { displayName: 'Fun (deprecated)', value: 'fun' },
                      { displayName: 'Exits (deprecated)', value: 'exit' },
                      { displayName: 'ELA (deprecated)', value: 'ela' },
                      {
                        displayName: 'AI Feedback (deprecated)',
                        value: 'feedback',
                      },
                      /* End of old categories */
                    ]}
                  />
                </Col>
              )}

              {isGenerator && (
                <>
                  <Col span={24}>
                    <Title $fontSize={18}>Standards</Title>
                    <MultiSelect
                      maxTagCount={1}
                      maxTagPlaceholder={omittedValues => {
                        const omittedTeksStandards = omittedValues.filter(standard =>
                          standard.label.includes('TEKS'),
                        );
                        const omittedCcssStandards = omittedValues.filter(standard =>
                          standard.label.includes('CCSS'),
                        );

                        return (
                          <Tooltip
                            overlayInnerStyle={{
                              display: 'flex !important',
                              flexDirection: 'column',
                              maxHeight: '200px',
                              overflow: 'auto',
                            }}
                            overlayStyle={{
                              display: 'flex !important',
                              flexDirection: 'column',
                            }}
                            trigger={['hover']}
                            title={
                              <>
                                {[
                                  ...orderTEKSELAStandards(omittedTeksStandards),
                                  ...orderCCSSELAStandards(omittedCcssStandards),
                                ].map(({ label }) => (
                                  <p key={label}>{label}</p>
                                ))}
                              </>
                            }
                          >
                            <p style={{ display: 'block' }}>+{omittedValues.length}...</p>
                          </Tooltip>
                        );
                      }}
                      style={{ fontSize: 16, paddingBottom: 3 }}
                      defaultValue={
                        generator?.learningStandards?.map(each => each.id) || []
                      }
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      filterOption={(inputValue: any, option: any) =>
                        option?.children
                          .toLowerCase()
                          .indexOf(inputValue.toLowerCase()) >= 0
                      }
                      onChange={e => {
                        setGeneratorSettings({
                          status: generatorSettings?.status || 'staging',
                          isPremiumGenerator:
                            generatorSettings?.isPremiumGenerator || false,
                          generatorPlan: generatorSettings?.generatorPlan || undefined,

                          learningStandards: e as string[],
                          durationInMins: generatorSettings?.durationInMins,
                        });
                      }}
                      options={orderedStandards.map(standard => ({
                        displayName: standard.label,
                        value: standard.id,
                      }))}
                    />
                  </Col>
                </>
              )}
              {isGenerator && (
                <Col span={24}>
                  <Divider />
                  <Title $fontSize={18}>Create standards</Title>
                  <ManageStandardsForm />
                  <Divider />
                </Col>
              )}
              <Col span={24}>
                <Title $fontSize={18}>
                  Download as PDF{' '}
                  <Tooltip
                    title={
                      'PDF does not support video or interactive questions. Consider starting the lesson to have students join your interactive lesson.'
                    }
                  >
                    <TooltipIconWrapper>
                      <QuestionMarkSvg />
                    </TooltipIconWrapper>
                  </Tooltip>
                </Title>

                <Button
                  type="ghost"
                  loading={isDownloadingPDF}
                  onClick={handleDownloadPDFClicked}
                >
                  {t('Download')}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <Row gutter={[20, 20]}>
              {isGenerator && isAITemplatesEnabled && (
                <Col span={24}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Title style={{ width: 'fit-content' }} $fontSize={18}>
                      {'Cover photo for customizable lesson'}
                      <Tooltip
                        title={'Add an .svg image to describe your customizable lesson'}
                      >
                        <TooltipIconWrapper>
                          <QuestionMarkSvg />
                        </TooltipIconWrapper>
                      </Tooltip>
                    </Title>
                    <UploadCoverImageLabel
                      htmlFor="coverImageId"
                      style={{
                        padding: '2px 5px',
                        marginTop: 5,
                        border: 'solid 1px black',
                        cursor: 'pointer',
                      }}
                    >
                      {!isGenerator ? 'Publish as preview first' : 'Choose file'}
                    </UploadCoverImageLabel>
                  </div>
                  {!localTemplate.coverImageId && (
                    <CoverImageWrapper>
                      <Image
                        src={emptyTemplateCoverPhoto}
                        width={'320px'}
                        height={'200px'}
                      />
                    </CoverImageWrapper>
                  )}
                  {localTemplate.coverImageId && (
                    <CoverImageWrapper>
                      {generatorCoverImageUrl && <img src={generatorCoverImageUrl}></img>}
                      {isCoverPhotoUploadSizeErrorVisible && (
                        <ImageUploadFileSizeError>{`Max file size ${MAX_GENERATOR_COVER_FILE_SIZE_MB} MB`}</ImageUploadFileSizeError>
                      )}
                    </CoverImageWrapper>
                  )}
                  <UploadCoverImage
                    data-testid="FileInput"
                    id={'coverImageId'}
                    type="file"
                    name="myFile"
                    multiple={false}
                    disabled={!isGenerator}
                    accept=".svg, .jpg, .jpeg, .png"
                    onChange={e => {
                      handleUploadGeneratorCoverImage(e);
                    }}
                  />
                </Col>
              )}
              {!isGenerator && (
                <Col span={24}>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Title $fontSize={18}>
                      {t('Cover photo')}
                      <Tooltip title={t('Add a photo to describe your public lesson')}>
                        <TooltipIconWrapper>
                          <QuestionMarkSvg />
                        </TooltipIconWrapper>
                      </Tooltip>
                    </Title>
                    <Button
                      type={'ghost'}
                      styles={{ padding: '2px 5px 2px 5px', margin: 0, marginRight: 25 }}
                      onClick={handleOpenCoverPhotoModal}
                    >
                      {t('Upload image')}
                    </Button>
                  </div>
                  {!localTemplate.coverImageId && (
                    <CoverImageWrapper onClick={handleOpenCoverPhotoModal}>
                      <Image
                        src={emptyTemplateCoverPhoto}
                        width={'320px'}
                        height={'200px'}
                      />
                    </CoverImageWrapper>
                  )}
                  {localTemplate.coverImageId && (
                    <CoverImageWrapper onClick={handleOpenCoverPhotoModal}>
                      <GameImageRenderer
                        fileId={localTemplate.coverImageId}
                        fileMimetype={'image/jpeg'}
                        fileAltText={localTemplate.coverImageAltText}
                        isAuthenticated={true}
                        isDiscoverTemplate={false}
                        isOwner={true}
                      />
                    </CoverImageWrapper>
                  )}
                </Col>
              )}

              {isGenerator && isAITemplatesEnabled && (
                <Col span={24}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Title style={{ width: 'fit-content' }} $fontSize={18}>
                      {'Background image'}
                    </Title>
                    <UploadBackgroundImageLabel
                      htmlFor="backgroundImageId"
                      style={{
                        padding: '2px 5px',
                        marginTop: 5,
                        border: 'solid 1px black',
                        cursor: 'pointer',
                      }}
                    >
                      {!isGenerator ? 'Publish as preview first' : 'Choose file'}
                    </UploadBackgroundImageLabel>
                  </div>
                  {!generator?.aiContent?.backgroundImageId && (
                    <BackgroundImageWrapper>
                      <Image
                        src={emptyTemplateCoverPhoto}
                        width={'320px'}
                        height={'200px'}
                      />
                    </BackgroundImageWrapper>
                  )}
                  {generator?.aiContent?.backgroundImageId && (
                    <BackgroundImageWrapper>
                      {generatorBackgroundImageUrl && (
                        <img src={generatorBackgroundImageUrl}></img>
                      )}
                      {isCoverPhotoUploadSizeErrorVisible && (
                        <ImageUploadFileSizeError>{`Max file size ${MAX_GENERATOR_COVER_FILE_SIZE_MB} MB`}</ImageUploadFileSizeError>
                      )}
                    </BackgroundImageWrapper>
                  )}
                  <UploadBackgroundImage
                    data-testid="BackgroundFileInput"
                    id={'backgroundImageId'}
                    type="file"
                    name="myFile"
                    multiple={false}
                    disabled={!isGenerator}
                    accept=".jpg, .jpeg, .png"
                    onChange={e => {
                      handleUploadGeneratorBackgroundImage(e);
                    }}
                  />
                </Col>
              )}

              {isGenerator && isAITemplatesEnabled && (
                <Col span={24}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Title style={{ width: 'fit-content' }} $fontSize={18}>
                      {'Lesson Duration (minutes)'}
                    </Title>
                  </div>

                  <Input
                    type="number"
                    onChange={e => {
                      setGeneratorSettings({
                        status: generatorSettings?.status || 'staging',
                        isPremiumGenerator:
                          generatorSettings?.isPremiumGenerator || false,
                        generatorPlan: generatorSettings?.generatorPlan || undefined,
                        learningStandards: generatorSettings?.learningStandards || [],

                        durationInMins: parseInt(e.target.value, 0),
                      });
                    }}
                    value={generator?.aiContent?.durationInMins || 0}
                  />
                </Col>
              )}

              <Col span={24}>
                <Title $fontSize={18}>
                  Share with teachers
                  <Tooltip
                    title={
                      isGenerator
                        ? 'Anyone with the link to the customizable lesson will be able to consume it. Please remember to publish the customizable lesson before sharing.'
                        : t(
                            'Anyone with access to this lesson can make a copy and continue to edit. Your lesson will always remain unchanged',
                          )
                    }
                  >
                    <TooltipIconWrapper>
                      <QuestionMarkSvg />
                    </TooltipIconWrapper>
                  </Tooltip>
                </Title>
                <SocialMediaContainer>
                  <Tooltip
                    title={copyTemplateSuccess ? t('Link copied') : t('Copy link')}
                  >
                    <div
                      id="copy_url_template"
                      data-clipboard-text={shareUrl}
                      style={{
                        height: 32,
                        width: 32,
                        borderRadius: 100,
                        background: theme.colors.orange,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconWrapper
                        color={theme.colors.white}
                        height={'16px'}
                        width={'16px'}
                      >
                        <LinkSvg />
                      </IconWrapper>
                    </div>
                  </Tooltip>
                  <TwitterShareButton
                    url={shareUrl}
                    title={`${shareTitle}`}
                    onClick={() => handleClickShareButton('twitter')}
                  >
                    <TwitterIcon size={32} round={true} />
                  </TwitterShareButton>
                  <LinkedinShareButton
                    url={shareUrl}
                    title={shareTitle}
                    onClick={() => handleClickShareButton('linkedin')}
                  >
                    <LinkedinIcon size={32} round={true} />
                  </LinkedinShareButton>
                  <FacebookShareButton
                    url={shareUrl}
                    quote={shareTitle}
                    onClick={() => handleClickShareButton('facebook')}
                  >
                    <FacebookIcon size={32} round={true} />
                  </FacebookShareButton>
                  <FacebookMessengerShareButton
                    url={shareUrl}
                    title={shareTitle}
                    appId="857396192234237"
                    onClick={() => handleClickShareButton('facebook')}
                  >
                    <FacebookMessengerIcon size={32} round={true} />
                  </FacebookMessengerShareButton>
                  <QRCodeShareButton url={shareUrl} />
                  <a
                    href={`mailto:?subject=${emailSubject}&body=${t(
                      'Below is a link to my Curipod lesson.',
                    )}\n\n\n ${shareUrl}`}
                    onClick={() => handleClickShareButton('email')}
                  >
                    <EmailIcon size={32} round={true} />
                  </a>
                  <WhatsappShareButton
                    url={shareUrl}
                    title={shareTitle}
                    onClick={() => handleClickShareButton('whatsapp')}
                  >
                    <WhatsappIcon size={32} round={true} />
                  </WhatsappShareButton>
                  <MSTeamsShareButton url={shareUrl} body={shareTitle} />
                </SocialMediaContainer>
              </Col>
              <Col span={24}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Title $fontSize={18}>
                    {t('Link Sharing')}
                    <Tooltip
                      title={'People with the lesson link can access this lesson.'}
                    >
                      <TooltipIconWrapper>
                        <QuestionMarkSvg />
                      </TooltipIconWrapper>
                    </Tooltip>
                  </Title>
                  {localTemplate && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Switch
                        color={theme.colors.primary}
                        value={!localTemplate?.isBlockedFromSharing}
                        onChange={e => {
                          handleIsBlockedFromSharingChange(!e); //negation because the switch is opposite
                        }}
                      />
                      <span style={{ marginLeft: 10, fontWeight: 'bold' }}>
                        {localTemplate?.isBlockedFromSharing
                          ? t(' Lesson is not sharable')
                          : t(' Lesson is sharable')}
                      </span>
                    </div>
                  )}
                </div>
              </Col>

              {isAITemplatesEnabled &&
                localTemplate.aiContent !== undefined &&
                localTemplate.aiContent?.categories !== undefined && (
                  <>
                    <Col span={24} style={{ marginTop: 3 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {isGenerator && <Title $fontSize={18}>Publish options</Title>}
                        {isGenerator && (
                          <>
                            <Select
                              style={{ fontSize: 16, paddingBottom: 3 }}
                              value={generatorSettings?.status}
                              onSelect={e =>
                                setGeneratorSettings({
                                  status: e as DiscoveredTemplateStatus,
                                  isPremiumGenerator:
                                    generatorSettings?.isPremiumGenerator || false,
                                  generatorPlan:
                                    generatorSettings?.generatorPlan || undefined,
                                  learningStandards:
                                    generatorSettings?.learningStandards || [],
                                  durationInMins: generatorSettings?.durationInMins,
                                })
                              }
                              disabled={generatorSettings?.status === 'rejected'}
                              options={generatorStatusOptions}
                            />
                          </>
                        )}
                      </div>
                    </Col>
                    <Col span={24} style={{ marginTop: 3 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {isGenerator && <Title $fontSize={18}>Generator plan</Title>}
                        {isGenerator && (
                          <>
                            <Select
                              style={{ fontSize: 16, paddingBottom: 3 }}
                              value={
                                generatorSettings?.generatorPlan
                                  ? generatorSettings?.generatorPlan
                                  : generatorSettings?.isPremiumGenerator
                                  ? GeneratorCoarseLicenseLevel.premium
                                  : GeneratorCoarseLicenseLevel.freemium
                              }
                              placeholder={'Freemium / Premium / Enterprise'}
                              onSelect={e =>
                                setGeneratorSettings({
                                  status: generatorSettings?.status || 'staging',
                                  isPremiumGenerator:
                                    e === GeneratorCoarseLicenseLevel.premium ||
                                    e === GeneratorCoarseLicenseLevel.enterprise
                                      ? true
                                      : false,
                                  generatorPlan:
                                    (e as GeneratorCoarseLicenseLevel) ||
                                    GeneratorCoarseLicenseLevel.freemium,
                                  learningStandards:
                                    generatorSettings?.learningStandards || [],
                                  durationInMins: generatorSettings?.durationInMins,
                                })
                              }
                              options={generatorPlanOptions}
                            />
                          </>
                        )}
                      </div>
                    </Col>
                  </>
                )}
            </Row>
          </Col>
        </Row>

        {isAITemplatesEnabled && !isGenerator && (
          <div
            style={{
              width: '100%',
              marginTop: '60px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              loading={isEditingOrCreatingGenerator}
              onClick={() => {
                saveGenerator({
                  generatorId: localTemplate.id,
                  status: 'staging',
                });
              }}
            >
              Enable generator publishing
            </Button>
          </div>
        )}
        <BottomActionsContainer>
          <Button
            onClick={closeModal}
            type="tertiary"
            styles={{ border: 'none', color: theme.colors.black }}
          >
            {t('Cancel')}
          </Button>
          {!isGenerator && (
            <Button onClick={handleSave} styles={{ marginLeft: 10 }} type="primary">
              {t('Done')}
            </Button>
          )}
          {isGenerator && (
            <Button
              loading={isEditingOrCreatingGenerator}
              onClick={() => {
                if (!generatorSettings) return;
                saveGenerator({
                  generatorId: localTemplate.id,
                  status: generatorSettings.status,
                  isPremiumGenerator: generatorSettings.isPremiumGenerator,
                  generatorPlan: generatorSettings.generatorPlan,
                  learningStandards: generatorSettings.learningStandards,
                  durationInMins: generatorSettings.durationInMins,
                  backgroundImageId: generator?.aiContent?.backgroundImageId || undefined,
                  onSuccess: () => {
                    syncSampleLessons();
                    closeModal();
                  },
                });
              }}
            >
              Save customizable lesson
            </Button>
          )}
        </BottomActionsContainer>
      </>
    </BaseModal>
  );
};

const SocialMediaContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 0.5em;
  & > *:not(:first-child) {
    margin-left: 2px;
  }
  & > *:not(:last-child) {
    margin-right: 2px;
  }
`;

const UploadCoverImage = styled.input`
  display: none;
`;

const UploadBackgroundImage = styled.input`
  display: none;
`;

const UploadCoverImageLabel = styled.label`
  background-color: ${theme.colors.greyLight};
  border-radius: 10px;
  padding: 10px;
  font-weight: bold;
`;

const UploadBackgroundImageLabel = styled.label`
  background-color: ${theme.colors.greyLight};
  border-radius: 10px;
  padding: 10px;
  font-weight: bold;
`;

const CoverImageWrapper = styled.div`
  img {
    height: 200px;
    width: 320px;
    object-fit: cover;
  }

  @media (max-width: 900px) {
    img {
      height: 100px;
      width: 200px;
    }
  }
`;

const BackgroundImageWrapper = styled.div`
  img {
    height: 200px;
    width: 320px;
    object-fit: cover;
  }

  @media (max-width: 900px) {
    img {
      height: 100px;
      width: 200px;
    }
  }
`;

const BottomActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
`;

export default SettingsModalDesigner;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  svg {
    height: 20px;
    width: 20px;
    color: ${theme.colors.black};
  }
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;
const ImageUploadFileSizeError = styled.div`
  color: ${theme.colors.red};
  margin-top: 5px;
`;
